import React, { useEffect, useState } from 'react';
import { Container, Grid, Card, CardContent, Typography, Box, IconButton, Tooltip } from '@mui/material';
import { Thermostat, Speed, DarkMode, LightMode } from '@mui/icons-material';
import SensorsIcon from '@mui/icons-material/Sensors';
import io from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import CircleProgress from './CircleProgress';
import ExpandableCard from './ExpandableCard';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://pochelo.online';
const socket = io(SOCKET_URL);

// Socket.io evento 'mqttMachine12' — publicado por el ESP32 via MQTT "Machine12/datos":
// { temperatura: number (LM35, °C), distancia: number (HC-SR04, cm, -1 = sin objeto),
//   rpm: number (E18-D80NK, interrupciones por minuto) }
const DIST_WARNING = 20;  // cm — distancia de alerta
const DIST_DANGER  = 5;   // cm — distancia de peligro

const Prueba = () => {
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('darkMode') !== 'false';
    });

    const toggleDarkMode = () => {
        setDarkMode(prev => {
            localStorage.setItem('darkMode', String(!prev));
            return !prev;
        });
    };

    const theme = {
        bg:          darkMode ? '#04091a' : '#f0f4f8',
        cardBg:      darkMode ? '#071525' : '#ffffff',
        cardBg2:     darkMode ? '#050e1e' : '#e8edf2',
        text:        darkMode ? '#B8F1FF' : '#1a2b3c',
        textMuted:   darkMode ? '#3a5a80' : '#6b7280',
        border:      darkMode ? 'rgba(30, 90, 180, 0.55)' : '#d1d5db',
        borderCard:  darkMode ? 'rgba(40, 120, 255, 0.28)' : 'rgba(0,0,0,0.1)',
        shadow: darkMode
            ? '0 8px 32px rgba(0,0,0,0.85), 0 2px 8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(60,140,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.55)'
            : '0 4px 16px rgba(0,0,0,0.1), 0 1px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.95)',
        shadowInset: darkMode
            ? 'inset 0 3px 12px rgba(0,0,0,0.75), inset 0 1px 4px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(0,0,50,0.4)'
            : 'inset 0 2px 6px rgba(0,0,0,0.07)',
    };

    const blink = keyframes`
        0% { opacity: 1; }
        50% { opacity: 0.2; }
        100% { opacity: 1; }
    `;

    const navigate = useNavigate();
    const [sensorData, setSensorData] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        const handleConnect = () => {
            console.log('Conectado a socket.io (Prueba)');
        };
        const handleMachine12Data = (data) => {
            console.log('[SOCKET][Machine12] Datos recibidos:', data);
            setSensorData(data);
            setLastUpdate(new Date().toISOString());
        };

        socket.on('connect', handleConnect);
        socket.on('mqttMachine12', handleMachine12Data);

        return () => {
            socket.off('connect', handleConnect);
            socket.off('mqttMachine12', handleMachine12Data);
        };
    }, [navigate]);

    const formatHour = (iso) => {
        if (!iso) return null;
        try {
            const d = new Date(iso);
            const h = String(d.getHours()).padStart(2, '0');
            const m = String(d.getMinutes()).padStart(2, '0');
            return `${h}:${m}`;
        } catch (e) { return null; }
    };

    const hasData = !!sensorData;
    const data = sensorData || {};

    const getDistanciaStyle = (valor) => {
        if (valor === undefined || valor === null || valor < 0) return { color: '#5f5d5dff' };
        if (valor <= DIST_DANGER)  return { color: '#ef4444', animation: `${blink} 0.5s infinite` };
        if (valor <= DIST_WARNING) return { color: '#f59e0b' };
        return { color: '#22c55e' };
    };
    const distanciaStyle = getDistanciaStyle(data.distancia);

    return (
        <Container maxWidth={false} sx={{ width: '99%', px: { xs: 0, sm: 2 }, '@media (min-width: 1441px)': { width: '90%', mx: 'auto' }, backgroundColor: theme.bg, minHeight: '100vh', transition: 'background-color 0.3s' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, pr: 1 }}>
                <Tooltip title={darkMode ? 'Modo claro' : 'Modo oscuro'}>
                    <IconButton
                        onClick={toggleDarkMode}
                        sx={{
                            backgroundColor: darkMode ? '#0d1f3a' : '#dbeafe',
                            color: darkMode ? '#B8F1FF' : '#1d4ed8',
                            border: `1px solid ${darkMode ? '#1e4a80' : '#93c5fd'}`,
                            boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(80,160,255,0.1)' : 'none',
                            '&:hover': { backgroundColor: darkMode ? '#142a4d' : '#bfdbfe' },
                            transition: 'all 0.3s',
                        }}
                    >
                        {darkMode ? <LightMode /> : <DarkMode />}
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ mt: 1, color: theme.text, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ mb: 2, color: theme.text, fontWeight: 'bold' }}>
                    Prueba de Sensores
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                    {/* LM35 - Temperatura */}
                    <Grid item xs={12} sm={6} md={4}>
                        <ExpandableCard modalWidth={{ xs: '80vw', md: '30vw' }} modalHeight={{ xs: '60vw', md: '22vw' }}>
                            <Card sx={{ backgroundColor: theme.cardBg, color: theme.text, minHeight: 260, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: theme.shadow, border: `1px solid ${theme.borderCard}`, borderRadius: '10px' }}>
                                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <Typography variant="h6" sx={{ borderBottom: `1px solid ${theme.border}`, pb: 1, mb: 1, fontWeight: 'bold' }}>
                                        LM35 - Temperatura
                                    </Typography>
                                    {hasData ? (
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Box sx={{ height: 110, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `1px solid ${theme.borderCard}`, borderRadius: '6px', backgroundColor: theme.cardBg2, boxShadow: theme.shadowInset, gap: 0.5 }}>
                                                <Thermostat sx={{ color: '#B8F1FF', fontSize: 22 }} />
                                                <CircleProgress value={Number(data.temperatura) || 0} min={0} max={100} size={60} strokeWidth={5} bgColor="transparent" textColor="#a2aab4" progressColor="#B8F1FF" displayText={`${Number(data.temperatura) || 0}°C`} />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography sx={{ color: '#adababff' }}>Sin datos</Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </ExpandableCard>
                    </Grid>

                    {/* HC-SR04 - Distancia */}
                    <Grid item xs={12} sm={6} md={4}>
                        <ExpandableCard modalWidth={{ xs: '80vw', md: '30vw' }} modalHeight={{ xs: '60vw', md: '22vw' }}>
                            <Card sx={{ backgroundColor: theme.cardBg, color: theme.text, minHeight: 260, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: theme.shadow, border: `1px solid ${theme.borderCard}`, borderRadius: '10px' }}>
                                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <Typography variant="h6" sx={{ borderBottom: `1px solid ${theme.border}`, pb: 1, mb: 1, fontWeight: 'bold' }}>
                                        HC-SR04 - Distancia
                                    </Typography>
                                    {hasData ? (
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                            <SensorsIcon sx={{ ...distanciaStyle, fontSize: 30 }} />
                                            <CircleProgress
                                                value={data.distancia >= 0 ? Number(data.distancia) : 0}
                                                min={0} max={400} size={90} strokeWidth={6}
                                                bgColor="transparent"
                                                textColor={distanciaStyle.color}
                                                progressColor={distanciaStyle.color}
                                                displayText={data.distancia < 0 ? '--' : `${Number(data.distancia).toFixed(1)} cm`}
                                            />
                                            <Typography sx={{ ...distanciaStyle, fontSize: 12 }}>
                                                {data.distancia < 0 ? 'Sin objeto' : data.distancia <= DIST_DANGER ? 'Muy cerca' : data.distancia <= DIST_WARNING ? 'Cerca' : 'Normal'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography sx={{ color: '#adababff' }}>Sin datos</Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </ExpandableCard>
                    </Grid>

                    {/* E18-D80NK - Conteo / RPM */}
                    <Grid item xs={12} sm={6} md={4}>
                        <ExpandableCard modalWidth={{ xs: '80vw', md: '30vw' }} modalHeight={{ xs: '60vw', md: '22vw' }}>
                            <Card sx={{ backgroundColor: theme.cardBg, color: theme.text, minHeight: 260, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: theme.shadow, border: `1px solid ${theme.borderCard}`, borderRadius: '10px' }}>
                                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <Typography variant="h6" sx={{ borderBottom: `1px solid ${theme.border}`, pb: 1, mb: 1, fontWeight: 'bold' }}>
                                        E18-D80NK - Conteo (RPM)
                                    </Typography>
                                    {hasData ? (
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                            <Speed sx={{ color: '#B8F1FF', fontSize: 30 }} />
                                            <CircleProgress value={Number(data.rpm) || 0} min={0} max={60} size={90} strokeWidth={6} bgColor="transparent" textColor="#a2aab4" progressColor="#B8F1FF" displayText={`${Number(data.rpm) || 0}`} />
                                            <Typography sx={{ color: theme.textMuted, fontSize: 12 }}>
                                                Interrupciones por minuto
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography sx={{ color: '#adababff' }}>Sin datos</Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </ExpandableCard>
                    </Grid>
                </Grid>
                {lastUpdate && (
                    <Typography variant="caption" sx={{ color: theme.textMuted, display: 'block', mt: 2 }}>
                        Última actualización: {formatHour(lastUpdate)}
                    </Typography>
                )}
            </Box>
        </Container>
    );
};

export default Prueba;
